export function latLngPathToArray(path) {
  if (!path || !path.getArray) return [];
  return path.getArray().map((latLng) => ({
    lat: latLng.lat(),
    lng: latLng.lng(),
  }));
}

function haversineDistanceMeters(pointA, pointB) {
  const R = 6371000;
  const lat1 = (pointA.lat * Math.PI) / 180;
  const lat2 = (pointB.lat * Math.PI) / 180;
  const dLat = lat2 - lat1;
  const dLng = ((pointB.lng - pointA.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toLatLngArray(points, maps) {
  if (maps && maps.LatLng) {
    return points.map((point) => new maps.LatLng(point.lat, point.lng));
  }
  return points;
}

export function computePointsLengthMeters(points, maps) {
  if (!points || points.length < 2) return 0;
  if (maps && maps.geometry && maps.geometry.spherical) {
    return maps.geometry.spherical.computeLength(toLatLngArray(points, maps));
  }
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    total += haversineDistanceMeters(points[index - 1], points[index]);
  }
  return total;
}

export function computePointsAreaHa(points, maps) {
  if (!points || points.length < 3) return 0;
  if (maps && maps.geometry && maps.geometry.spherical) {
    return (
      maps.geometry.spherical.computeArea(toLatLngArray(points, maps)) / 10000
    );
  }

  const lats = points.map((point) => point.lat);
  const lons = points.map((point) => point.lng);
  const avgLat = lats.reduce((sum, lat) => sum + lat, 0) / lats.length;
  const mPerDegLat = 110574.0;
  const mPerDegLon = 111320.0 * Math.abs(Math.cos((avgLat * Math.PI) / 180));
  const ptsM = points.map((point) => ({
    x: point.lng * mPerDegLon,
    y: point.lat * mPerDegLat,
  }));

  let area = 0;
  for (let index = 0; index < ptsM.length; index += 1) {
    const next = (index + 1) % ptsM.length;
    area += ptsM[index].x * ptsM[next].y - ptsM[next].x * ptsM[index].y;
  }

  return Math.abs(area / 2) / 10000;
}

export function computePointsPerimeterM(points, maps) {
  if (!points || points.length < 3) {
    return computePointsLengthMeters(points, maps);
  }
  return computePointsLengthMeters(closePolygonRing(points), maps);
}

export function computePathLengthMeters(maps, path) {
  if (!path) return 0;
  if (maps && maps.geometry && maps.geometry.spherical && path.getArray) {
    return maps.geometry.spherical.computeLength(path);
  }
  return computePointsLengthMeters(latLngPathToArray(path), maps);
}

export function computePolygonAreaHa(maps, path) {
  if (!path) return 0;
  if (maps && maps.geometry && maps.geometry.spherical && path.getArray) {
    return maps.geometry.spherical.computeArea(path) / 10000;
  }
  return computePointsAreaHa(latLngPathToArray(path), maps);
}

export function computePolygonPerimeterM(maps, path) {
  if (!path) return 0;
  if (maps && maps.geometry && maps.geometry.spherical && path.getArray) {
    return maps.geometry.spherical.computeLength(path);
  }
  return computePointsPerimeterM(latLngPathToArray(path), maps);
}

export function formatDistanceM(meters) {
  const value = Number(meters) || 0;
  if (value >= 1000) {
    return `${(value / 1000).toFixed(2)} km`;
  }
  return `${value.toFixed(1)} m`;
}

export function formatAreaHa(hectares) {
  const value = Number(hectares) || 0;
  return `${value.toFixed(2)} ha`;
}

export function closePolygonRing(points) {
  if (!points || points.length === 0) return [];
  const ring = points.slice();
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first.lat !== last.lat || first.lng !== last.lng) {
    ring.push({ ...first });
  }
  return ring;
}

function ringSignedArea(points) {
  const ring = ringToEditablePoints(closePolygonRing(points));
  if (ring.length < 3) return 0;
  let area = 0;
  for (let index = 0; index < ring.length; index += 1) {
    const next = (index + 1) % ring.length;
    area += ring[index].lng * ring[next].lat;
    area -= ring[next].lng * ring[index].lat;
  }
  return area / 2;
}

/** Anel externo CCW; recortes internos CW (GeoJSON / PostGIS / Google Maps). */
export function normalizePolygonRings(outerPoints, holeRings = []) {
  const outerOpen = ringToEditablePoints(closePolygonRing(outerPoints || []));
  if (outerOpen.length >= 3 && ringSignedArea(outerOpen) < 0) {
    outerOpen.reverse();
  }

  const outerWindingPositive = ringSignedArea(outerOpen) > 0;
  const holes = (holeRings || []).map((hole) => {
    const holeOpen = ringToEditablePoints(closePolygonRing(hole || []));
    if (holeOpen.length < 3) return holeOpen;
    const holeWindingPositive = ringSignedArea(holeOpen) > 0;
    if (holeWindingPositive === outerWindingPositive) {
      holeOpen.reverse();
    }
    return holeOpen;
  });

  return { outer: outerOpen, holes };
}

export function pathToLineWkt(points) {
  const coords = points.map((point) => `${point.lng} ${point.lat}`).join(", ");
  return `SRID=4326;LINESTRING (${coords})`;
}

export function pathToPolygonWkt(points) {
  const ring = closePolygonRing(points);
  const coords = ring.map((point) => `${point.lng} ${point.lat}`).join(", ");
  return `SRID=4326;POLYGON ((${coords}))`;
}

export function computePolygonWithHolesAreaHa(outerPoints, holeRings, maps) {
  const outerHa = computePointsAreaHa(outerPoints, maps);
  const holesHa = (holeRings || []).reduce(
    (sum, ring) => sum + computePointsAreaHa(ring, maps),
    0
  );
  return Math.max(0, outerHa - holesHa);
}

export function pathToPolygonWithHolesWkt(outerPoints, holeRings) {
  const { outer, holes } = normalizePolygonRings(outerPoints, holeRings);
  const outerCoords = closePolygonRing(outer)
    .map((point) => `${point.lng} ${point.lat}`)
    .join(", ");
  const holeParts = holes
    .filter((hole) => hole.length >= 3)
    .map((hole) => {
      const coords = closePolygonRing(hole)
        .map((point) => `${point.lng} ${point.lat}`)
        .join(", ");
      return `(${coords})`;
    });
  if (!holeParts.length) {
    return pathToPolygonWkt(outer);
  }
  return `SRID=4326;POLYGON ((${outerCoords}), ${holeParts.join(", ")})`;
}

export function buildPolygonShapeMetrics(outerPoints, holeRings, maps) {
  return {
    areaHa: computePolygonWithHolesAreaHa(outerPoints, holeRings, maps),
    perimeterM: computePointsPerimeterM(outerPoints, maps),
    polygonWkt: pathToPolygonWithHolesWkt(outerPoints, holeRings),
  };
}

function holeCentroid(hole) {
  const points = ringToEditablePoints(closePolygonRing(hole || []));
  if (!points.length) return null;
  return {
    lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
    lng: points.reduce((sum, point) => sum + point.lng, 0) / points.length,
  };
}

export function validateHolesInsideOuter(outerPoints, holeRings) {
  const outer = ringToEditablePoints(closePolygonRing(outerPoints || []));
  if (outer.length < 3) {
    return "O contorno do talhão precisa de pelo menos 3 pontos.";
  }

  const invalidIndex = (holeRings || []).findIndex((hole) => {
    if (!hole || hole.length < 3) return true;
    const ring = ringToEditablePoints(closePolygonRing(hole));
    return ring.some((point) => !pointInPolygon(point, outer));
  });

  if (invalidIndex >= 0) {
    return `O recorte ${invalidIndex + 1} precisa ficar totalmente dentro do talhão.`;
  }

  return null;
}

export function countWktInteriorRings(polygonWkt) {
  if (!polygonWkt || typeof polygonWkt !== "string") return 0;
  const match = polygonWkt.match(/POLYGON\s*\(\((.*)\)\)/i);
  if (!match) return 0;
  const body = match[1];
  let depth = 0;
  let ringStarts = 0;
  for (let index = 0; index < body.length; index += 1) {
    const char = body[index];
    if (char === "(") {
      if (depth === 0) ringStarts += 1;
      depth += 1;
    } else if (char === ")") {
      depth -= 1;
    }
  }
  return Math.max(0, ringStarts - 1);
}

export function parseLandplotPolygon(landplot) {
  if (!landplot) return { outer: [], holes: [] };
  try {
    const geometry = JSON.parse(landplot.geomap);
    const rings = Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
    const toPoints = (ring) =>
      (ring || []).map((item) => ({
        lat: item[1],
        lng: item[0],
      }));
    return {
      outer: toPoints(rings[0]),
      holes: rings.slice(1).map(toPoints),
    };
  } catch (error) {
    return { outer: [], holes: [] };
  }
}

export function parseLandplotRing(landplot) {
  return parseLandplotPolygon(landplot).outer;
}

/** Remove vértice de fechamento duplicado para edição no mapa. */
export function ringToEditablePoints(ring) {
  if (!ring || ring.length < 2) return ring || [];
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first.lat === last.lat && first.lng === last.lng) {
    return ring.slice(0, -1);
  }
  return ring;
}

export const LAND_PLOT_MAP_STYLE = {
  fillColor: "#A5D6A7",
  fillOpacity: 0.2,
  strokeColor: "#81C784",
  strokeOpacity: 0.75,
  strokeWeight: 2,
};

export const LAND_PLOT_MAP_STYLE_HIGHLIGHTED = {
  fillColor: "#A5D6A7",
  fillOpacity: 0.32,
  strokeColor: "#FFD600",
  strokeOpacity: 1,
  strokeWeight: 4,
};

export function getLandPlotMapStyle(highlighted = false) {
  return highlighted ? LAND_PLOT_MAP_STYLE_HIGHLIGHTED : LAND_PLOT_MAP_STYLE;
}

const CROP_COLORS = [
  "#2E7D32",
  "#558B2F",
  "#F9A825",
  "#EF6C00",
  "#6A1B9A",
  "#00838F",
  "#AD1457",
  "#4527A0",
  "#283593",
  "#00695C",
];

export function cropColorFromName(name, indexFallback = 0) {
  const label = (name || "").trim();
  if (!label) {
    return CROP_COLORS[indexFallback % CROP_COLORS.length];
  }
  let hash = 0;
  for (let i = 0; i < label.length; i += 1) {
    hash = label.charCodeAt(i) + ((hash << 5) - hash);
  }
  return CROP_COLORS[Math.abs(hash) % CROP_COLORS.length];
}

export function cropDisplayName(landplot) {
  if (!landplot) return "";
  return (landplot.crop_name || landplot.name || "").trim();
}

export function formatPlotNameHectares(areaHa) {
  const hectares = Number(areaHa);
  if (!Number.isFinite(hectares) || hectares < 0) {
    return "0";
  }
  return String(Math.round(hectares));
}

export function buildPlotName(baseName, areaHa) {
  const base = (baseName || "").trim();
  const formattedHa = formatPlotNameHectares(areaHa);
  if (!base) {
    return `${formattedHa} ha`;
  }
  return `${base} — ${formattedHa} ha`;
}

export function stripPlotAreaSuffix(value) {
  const text = String(value == null ? "" : value).trim();
  if (!text) return "";
  const match = text.match(/^(.+?) — [\d.,]+\s*ha$/i);
  return match ? match[1].trim() : text;
}

function isAreaOnlyPlotLabel(value) {
  return /^[\d.,]+(\s*ha)?$/i.test(String(value || "").trim());
}

export function getPlotBaseName(landplot) {
  if (!landplot) return "";
  const fromName = stripPlotAreaSuffix(landplot.name);
  const fromCrop = stripPlotAreaSuffix(landplot.crop_name);
  const nameUsable = Boolean(fromName) && !isAreaOnlyPlotLabel(fromName);
  const cropUsable = Boolean(fromCrop) && !isAreaOnlyPlotLabel(fromCrop);

  if (nameUsable && cropUsable) {
    if (fromName === fromCrop) return fromName;
    if (fromName.length > fromCrop.length) return fromName;
    return fromCrop;
  }
  if (nameUsable) return fromName;
  if (cropUsable) return fromCrop;
  return fromName || fromCrop || "";
}

export function resolveExistingPlotName(landplot) {
  if (!landplot) return "";
  const savedName = String(landplot.name || "").trim();
  if (savedName) return savedName;
  return getPlotBaseName(landplot);
}

export function getPlotDisplayLabel(landplot) {
  if (!landplot) return "";
  const base = getPlotBaseName(landplot);
  const formattedHa = formatPlotNameHectares(landplot.area_h2);
  if (!base) {
    return formattedHa;
  }
  return `${base} — ${formattedHa}`;
}

export function getPlotMapLabel(landplot) {
  if (!landplot) return "";
  return `${getPlotDisplayLabel(landplot)} ha`;
}

export function getLandplotCentroid(landplot) {
  if (!landplot) return null;
  if (landplot.landplot_center && landplot.landplot_center.lat != null) {
    return {
      lat: Number(landplot.landplot_center.lat),
      lng: Number(landplot.landplot_center.lng),
    };
  }
  if (landplot.center && landplot.center.lat != null) {
    return {
      lat: Number(landplot.center.lat),
      lng: Number(landplot.center.lng),
    };
  }
  const ring = parseLandplotRing(landplot);
  if (!ring.length) return null;
  const lat =
    ring.reduce((sum, point) => sum + point.lat, 0) / ring.length;
  const lng =
    ring.reduce((sum, point) => sum + point.lng, 0) / ring.length;
  return { lat, lng };
}

/** Teste ponto-dentro-do-polígono (anel aberto ou fechado). */
export function pointInPolygon(point, polygonRing) {
  if (!point || !polygonRing || polygonRing.length < 3) return false;
  const x = point.lng;
  const y = point.lat;
  let inside = false;
  for (let i = 0, j = polygonRing.length - 1; i < polygonRing.length; j = i, i += 1) {
    const xi = polygonRing[i].lng;
    const yi = polygonRing[i].lat;
    const xj = polygonRing[j].lng;
    const yj = polygonRing[j].lat;
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 0.0) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function landPlotsInsideRing(landPlots, polygonRing) {
  if (!Array.isArray(landPlots) || !polygonRing || polygonRing.length < 3) {
    return [];
  }
  return landPlots.filter((plot) => {
    const centroid = getLandplotCentroid(plot);
    return centroid ? pointInPolygon(centroid, polygonRing) : false;
  });
}

function isBrazilLatitude(value) {
  return Number.isFinite(value) && value >= -35 && value <= 6;
}

function isBrazilLongitude(value) {
  return Number.isFinite(value) && value >= -75 && value <= -30;
}

export function normalizeFarmMapRing(ring) {
  const points = (ring || []).filter(
    (point) =>
      point &&
      Number.isFinite(Number(point.lat)) &&
      Number.isFinite(Number(point.lng))
  );
  if (points.length < 3) return points;

  const sample = points[0];
  const lat = Number(sample.lat);
  const lng = Number(sample.lng);
  const storedAsLatLng =
    !isBrazilLatitude(lat) &&
    isBrazilLatitude(lng) &&
    isBrazilLongitude(lat);

  if (!storedAsLatLng) return points;
  return points.map((point) => ({
    lat: Number(point.lng),
    lng: Number(point.lat),
  }));
}

export function parseBoundaryGeoJsonRing(boundaryGeojson) {
  if (!boundaryGeojson || !boundaryGeojson.coordinates) return [];
  const ring = boundaryGeojson.coordinates[0];
  if (!Array.isArray(ring) || !Array.isArray(ring[0])) return [];
  return normalizeFarmMapRing(
    ring.map((coord) => ({
      lat: Number(coord[1]),
      lng: Number(coord[0]),
    }))
  );
}

export function isGroupSubFarm(farm) {
  return Boolean(farm && farm.parent);
}

export function mergeLandPlotLists(...lists) {
  const byId = new Map();
  lists.forEach((list) => {
    (Array.isArray(list) ? list : []).forEach((plot) => {
      if (!plot || plot.id == null) return;
      const existing = byId.get(plot.id);
      byId.set(plot.id, existing ? { ...existing, ...plot } : plot);
    });
  });
  return Array.from(byId.values());
}

export function resolveSubFarmIdForPlot(plot, groupFarms) {
  if (!plot || !Array.isArray(groupFarms)) return null;
  const subFarms = groupFarms.filter(isGroupSubFarm);
  if (plot.farm != null) {
    const assigned = subFarms.find(
      (farm) => String(farm.id) === String(plot.farm)
    );
    if (assigned) return assigned.id;
  }
  for (const farm of subFarms) {
    const ring = parseBoundaryGeoJsonRing(farm.boundary_geojson);
    if (ring.length < 3) continue;
    const centroid = getLandplotCentroid(plot);
    if (centroid && pointInPolygon(centroid, ring)) {
      return farm.id;
    }
  }
  return null;
}

export function landPlotIdsForSubFarm(plots, subFarmId, groupFarms) {
  const subFarm = (groupFarms || []).find(
    (farm) => String(farm.id) === String(subFarmId)
  );
  if (!subFarm) return [];

  const list = Array.isArray(plots) ? plots : [];
  const ids = new Set();

  list.forEach((plot) => {
    if (String(plot.farm) === String(subFarmId)) {
      ids.add(plot.id);
    }
  });

  const ring = parseBoundaryGeoJsonRing(subFarm.boundary_geojson);
  if (ring.length >= 3) {
    landPlotsInsideRing(list, ring).forEach((plot) => ids.add(plot.id));
  }

  return Array.from(ids);
}

function sortPlotsByDisplayName(plots) {
  return (plots || []).slice().sort((first, second) =>
    String(getPlotDisplayLabel(first) || "").localeCompare(
      String(getPlotDisplayLabel(second) || ""),
      "pt-BR",
      { numeric: true, sensitivity: "base" }
    )
  );
}

export function groupPlotsBySubFarm(
  plots,
  groupFarms,
  preferredFarmIds = []
) {
  const list = Array.isArray(plots) ? plots : [];
  const farms = (groupFarms || []).filter(isGroupSubFarm);
  const buckets = new Map();
  farms.forEach((farm) => {
    buckets.set(String(farm.id), []);
  });
  const unassigned = [];

  list.forEach((plot) => {
    const farmId = resolveSubFarmIdForPlot(plot, groupFarms);
    const key = farmId != null ? String(farmId) : "";
    if (key && buckets.has(key)) {
      buckets.get(key).push(plot);
      return;
    }
    unassigned.push(plot);
  });

  const preferred = [];
  const seen = new Set();
  (preferredFarmIds || []).forEach((id) => {
    const key = String(id);
    if (seen.has(key) || !buckets.has(key)) return;
    seen.add(key);
    const farmPlots = sortPlotsByDisplayName(buckets.get(key));
    if (!farmPlots.length) return;
    preferred.push({
      farm: farms.find((item) => String(item.id) === key) || null,
      plots: farmPlots,
    });
  });

  const rest = farms
    .filter((farm) => !seen.has(String(farm.id)))
    .map((farm) => ({
      farm,
      plots: sortPlotsByDisplayName(buckets.get(String(farm.id))),
    }))
    .filter((group) => group.plots.length);

  const groups = preferred.concat(rest);
  if (unassigned.length) {
    groups.push({ farm: null, plots: sortPlotsByDisplayName(unassigned) });
  }
  return groups;
}

export function sumLandPlotAreaHa(landPlots, plotIds) {
  const idSet = new Set((plotIds || []).map(String));
  return (landPlots || []).reduce((total, plot) => {
    if (!idSet.has(String(plot.id))) return total;
    const value = Number(String(plot.area_h2 || "").replace(",", "."));
    return total + (Number.isFinite(value) ? value : 0);
  }, 0);
}

function parsePlotAreaHa(plot) {
  const value = Number(String((plot && plot.area_h2) || "").replace(",", "."));
  return Number.isFinite(value) ? value : 0;
}

export function computeRingCentroid(ring) {
  const points = ringToEditablePoints(ring);
  if (!points.length) return null;
  return {
    lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
    lng: points.reduce((sum, point) => sum + point.lng, 0) / points.length,
  };
}

/** Aproximação visual/métrica de anel interno para cerca limpa (m). */
export function insetRingTowardCentroid(ring, widthM, maps) {
  const points = ringToEditablePoints(ring);
  if (points.length < 3 || !widthM || widthM <= 0) return points;

  const centroid = computeRingCentroid(points);
  if (!centroid) return points;

  const perimeterM = computePointsPerimeterM(closePolygonRing(points), maps);
  const avgHalfWidth = perimeterM / Math.max(8, points.length * 2);
  const insetRatio = Math.max(
    0.05,
    Math.min(0.95, 1 - widthM / Math.max(avgHalfWidth, widthM))
  );

  return points.map((point) => ({
    lat: centroid.lat + (point.lat - centroid.lat) * insetRatio,
    lng: centroid.lng + (point.lng - centroid.lng) * insetRatio,
  }));
}

/** Aproximação visual de anel externo para faixa de cerca limpa (m). */
export function outsetRingFromCentroid(ring, widthM, maps) {
  const points = ringToEditablePoints(ring);
  if (points.length < 3 || !widthM || widthM <= 0) return points;

  const centroid = computeRingCentroid(points);
  if (!centroid) return points;

  const perimeterM = computePointsPerimeterM(closePolygonRing(points), maps);
  const avgHalfWidth = perimeterM / Math.max(8, points.length * 2);
  const outsetRatio = Math.max(
    1.05,
    Math.min(2, 1 + widthM / Math.max(avgHalfWidth, widthM))
  );

  return points.map((point) => ({
    lat: centroid.lat + (point.lat - centroid.lat) * outsetRatio,
    lng: centroid.lng + (point.lng - centroid.lng) * outsetRatio,
  }));
}

/**
 * Área de trabalho = talhão completo. Faixa de cerca limpa é somada fora do
 * perímetro (não reduz a área interna). totalHa = workHa + fenceHa.
 */
export function computeCleanFenceSplit(ring, widthM, totalHa, maps) {
  const workHa = Number(totalHa) || 0;
  const width = Number(widthM) || 0;
  const perimeterM = computePointsPerimeterM(closePolygonRing(ring), maps);

  if (!width || width <= 0 || workHa <= 0 || ring.length < 3) {
    return {
      perimeterM,
      workHa,
      fenceHa: 0,
      totalHa: workHa,
      outerRing: ringToEditablePoints(ring),
    };
  }

  const fenceHa = (perimeterM * width) / 10000;
  const outerRing = outsetRingFromCentroid(ring, width, maps);

  return {
    perimeterM,
    workHa,
    fenceHa,
    totalHa: workHa + fenceHa,
    outerRing,
  };
}

export function summarizeCleanFenceSelection(landPlots, plotIds, widthM, maps) {
  const idSet = new Set((plotIds || []).map(String));
  let workHa = 0;
  let fenceHa = 0;
  let perimeterM = 0;
  const plots = [];

  (landPlots || []).forEach((plot) => {
    if (!idSet.has(String(plot.id))) return;
    const ring = parseLandplotRing(plot);
    const plotTotalHa = parsePlotAreaHa(plot);
    const split = computeCleanFenceSplit(ring, widthM, plotTotalHa, maps);
    workHa += split.workHa;
    fenceHa += split.fenceHa;
    perimeterM += split.perimeterM;
    plots.push({
      plotId: plot.id,
      name: plot.name,
      ...split,
    });
  });

  const cleanFenceWidthM = Number(widthM) || 0;
  const totalHa = workHa + fenceHa;

  return {
    count: plots.length,
    plotCount: plots.length,
    totalHa,
    workHa,
    fenceHa,
    perimeterM,
    cleanFenceWidthM,
    plots,
  };
}

const MANUAL_PIECE_COLORS = [
  { stroke: "#1565C0", fill: "#42A5F5" },
  { stroke: "#2E7D32", fill: "#66BB6A" },
  { stroke: "#EF6C00", fill: "#FFB74D" },
  { stroke: "#6A1B9A", fill: "#BA68C8" },
  { stroke: "#C62828", fill: "#E57373" },
  { stroke: "#00838F", fill: "#4DD0E1" },
];

export function manualPieceColor(index) {
  return MANUAL_PIECE_COLORS[index % MANUAL_PIECE_COLORS.length];
}

export function summarizeManualApplicationPieces(manualPieces) {
  const pieces = Array.isArray(manualPieces) ? manualPieces : [];
  const manualHa = pieces.reduce(
    (sum, piece) => sum + (Number(piece.areaHa) || 0),
    0
  );
  const manualPerimeterM = pieces.reduce(
    (sum, piece) => sum + (Number(piece.perimeterM) || 0),
    0
  );
  return {
    manualPieces: pieces,
    manualPieceCount: pieces.length,
    manualHa,
    manualPerimeterM,
  };
}

export function mergeApplicationAreaSummary(baseSummary, manualPieces) {
  const manual = summarizeManualApplicationPieces(manualPieces);
  const workHa = Number(baseSummary && baseSummary.workHa) || 0;
  const fenceHa = Number(baseSummary && baseSummary.fenceHa) || 0;
  const plotCount = Number(baseSummary && baseSummary.plotCount) || 0;
  const cleanFenceWidthM =
    Number(baseSummary && baseSummary.cleanFenceWidthM) || 0;

  return {
    ...(baseSummary || {}),
    ...manual,
    workHa,
    fenceHa,
    plotCount,
    cleanFenceWidthM,
    totalHa: workHa + fenceHa + manual.manualHa,
  };
}

export function manualPiecesFromPlanApplication(app) {
  if (!app || !Array.isArray(app.manual_pieces)) return [];
  return app.manual_pieces.map((piece, index) => ({
    id: piece.id || `manual-plan-${index}`,
    label: piece.label || `Trecho ${index + 1}`,
    areaHa: Number(piece.area_ha) || 0,
    perimeterM: Number(piece.perimeter_m) || 0,
    polygonWkt: piece.polygon_wkt || "",
    points: Array.isArray(piece.points) ? piece.points : [],
  }));
}

export function manualPiecesToPlanPayload(manualPieces) {
  return (manualPieces || []).map((piece, index) => ({
    id: piece.id || `manual-${index + 1}`,
    label: piece.label || `Trecho ${index + 1}`,
    area_ha: Number(piece.areaHa) || 0,
    perimeter_m: Number(piece.perimeterM) || 0,
    polygon_wkt: piece.polygonWkt || "",
  }));
}

function pointsBounds(points) {
  if (!points || !points.length) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  points.forEach((point) => {
    const lat = Number(point.lat);
    const lng = Number(point.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  });
  if (!Number.isFinite(minLat) || !Number.isFinite(maxLat)) return null;
  return { minLat, maxLat, minLng, maxLng };
}

function boundsDiagonalMeters(bounds) {
  if (!bounds) return 0;
  return haversineDistanceMeters(
    { lat: bounds.minLat, lng: bounds.minLng },
    { lat: bounds.maxLat, lng: bounds.maxLng }
  );
}

function unionBounds(first, second) {
  if (!first) return second;
  if (!second) return first;
  return {
    minLat: Math.min(first.minLat, second.minLat),
    maxLat: Math.max(first.maxLat, second.maxLat),
    minLng: Math.min(first.minLng, second.minLng),
    maxLng: Math.max(first.maxLng, second.maxLng),
  };
}

export function collectSubFarmMapPoints(farm, plots, groupFarms) {
  if (!farm) return [];
  const ringPoints = ringToEditablePoints(
    parseBoundaryGeoJsonRing(farm.boundary_geojson)
  );
  if (ringPoints.length >= 3) return ringPoints;

  return landPlotIdsForSubFarm(plots, farm.id, groupFarms)
    .map((plotId) =>
      (plots || []).find((plot) => String(plot.id) === String(plotId))
    )
    .map(getLandplotCentroid)
    .filter(Boolean);
}

export function areSubFarmSelectionsNearby(
  selectedFarms,
  nextFarm,
  plots,
  groupFarms
) {
  const currentPoints = (selectedFarms || []).flatMap((farm) =>
    collectSubFarmMapPoints(farm, plots, groupFarms)
  );
  const nextPoints = collectSubFarmMapPoints(nextFarm, plots, groupFarms);
  if (!currentPoints.length || !nextPoints.length) return false;

  const currentBounds = pointsBounds(currentPoints);
  const nextBounds = pointsBounds(nextPoints);
  const currentDiag = Math.max(boundsDiagonalMeters(currentBounds), 400);
  const nextDiag = Math.max(boundsDiagonalMeters(nextBounds), 400);
  const unionDiag = boundsDiagonalMeters(
    unionBounds(currentBounds, nextBounds)
  );
  const larger = Math.max(currentDiag, nextDiag);
  return unionDiag <= larger * 2.4 && unionDiag <= larger + 8000;
}

function boundsCenter(bounds) {
  if (!bounds) return null;
  return {
    lat: (bounds.minLat + bounds.maxLat) / 2,
    lng: (bounds.minLng + bounds.maxLng) / 2,
  };
}

function readMapCenter(map) {
  if (!map || !map.getCenter) return null;
  const center = map.getCenter();
  if (!center) return null;
  const lat = typeof center.lat === "function" ? center.lat() : center.lat;
  const lng = typeof center.lng === "function" ? center.lng() : center.lng;
  if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return null;
  return { lat: Number(lat), lng: Number(lng) };
}

export function scaleKmToMapZoom(scaleKm, latitude = -21) {
  const safeScale = Math.max(0.2, Number(scaleKm) || 1);
  const metersPerPixel = (safeScale * 1000) / 50;
  const latRad = (Number(latitude) * Math.PI) / 180;
  const zoom = Math.log2(
    (156543.03392 * Math.cos(latRad)) / metersPerPixel
  );
  if (!Number.isFinite(zoom)) return 13;
  return zoom;
}

export function resolveFarmMapFlyOverviewZoom({
  distanceMeters = 0,
  currentZoom,
  latitude = -21,
} = {}) {
  const distanceKm = Math.max(0, Number(distanceMeters) || 0) / 1000;
  const overviewScaleKm = Math.min(40, Math.max(1, 1 + distanceKm * 0.2));
  const overviewZoom = scaleKmToMapZoom(overviewScaleKm, latitude);
  const maxZoom = Number.isFinite(Number(currentZoom))
    ? Number(currentZoom) - 0.45
    : 13;
  return Math.max(7, Math.min(13, maxZoom, overviewZoom));
}

export function easeInOutCubic(progress) {
  const t = Math.max(0, Math.min(1, progress));
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export function quadraticBezier(start, mid, end, t) {
  const u = 1 - t;
  return u * u * start + 2 * u * t * mid + t * t * end;
}

export function midpointLatLng(start, end) {
  return {
    lat: (start.lat + end.lat) / 2,
    lng: (start.lng + end.lng) / 2,
  };
}

export function resolveFarmMapFlyDurationMs(distanceMeters) {
  const distanceKm = Math.max(0, Number(distanceMeters) || 0) / 1000;
  return Math.max(1100, Math.min(2800, 1100 + distanceKm * 90));
}

export function zoomToFitBounds(bounds, mapSize, padding = 40) {
  if (!bounds) return 16;
  const width = Math.max(
    80,
    Number(mapSize && mapSize.width) - padding * 2 || 720
  );
  const height = Math.max(
    80,
    Number(mapSize && mapSize.height) - padding * 2 || 520
  );
  const latSpan = Math.max(1e-6, bounds.maxLat - bounds.minLat);
  const lngSpan = Math.max(1e-6, bounds.maxLng - bounds.minLng);
  const latitude = (bounds.minLat + bounds.maxLat) / 2;
  const latRad = (latitude * Math.PI) / 180;
  const metersPerLng = 111320 * Math.max(0.2, Math.cos(latRad));
  const spanX = lngSpan * metersPerLng;
  const spanY = latSpan * 111320;
  const zoomX = Math.log2((156543.03392 * Math.cos(latRad) * width) / spanX);
  const zoomY = Math.log2((156543.03392 * Math.cos(latRad) * height) / spanY);
  const zoom = Math.min(zoomX, zoomY);
  if (!Number.isFinite(zoom)) return 16;
  return Math.max(7, Math.min(18, zoom));
}

export function interpolateFarmMapFlyCamera(progress, start, mid, end) {
  const t = easeInOutCubic(progress);
  const alongZoom = start.zoom + (end.zoom - start.zoom) * t;
  const valley = Math.sin(t * Math.PI);
  return {
    lat: quadraticBezier(start.lat, mid.lat, end.lat, t),
    lng: quadraticBezier(start.lng, mid.lng, end.lng, t),
    zoom: alongZoom + (mid.zoom - alongZoom) * valley,
  };
}

function readMapSize(map) {
  const node = map && map.getDiv ? map.getDiv() : null;
  return {
    width: (node && node.clientWidth) || 800,
    height: (node && node.clientHeight) || 560,
  };
}

function applyMapCamera(map, camera) {
  if (!map || !camera) return;
  if (map.moveCamera) {
    map.moveCamera({
      center: { lat: camera.lat, lng: camera.lng },
      zoom: camera.zoom,
    });
    return;
  }
  if (map.setCenter) {
    map.setCenter({ lat: camera.lat, lng: camera.lng });
  }
  if (map.setZoom) {
    map.setZoom(camera.zoom);
  }
}

function applyGoogleMapFit(map, maps, points, padding) {
  if (points.length === 1) {
    map.setCenter(points[0]);
    if (map.getZoom && map.getZoom() < 15) {
      map.setZoom(16);
    }
    return true;
  }

  const bounds = new maps.LatLngBounds();
  points.forEach((point) => {
    bounds.extend(new maps.LatLng(point.lat, point.lng));
  });
  map.fitBounds(bounds, padding);
  return true;
}

function runFarmMapFly(map, maps, points, padding, isCancelled) {
  const currentCenter = readMapCenter(map);
  const currentZoom = map.getZoom ? map.getZoom() : null;
  const targetBounds = pointsBounds(points);
  const targetCenter = boundsCenter(targetBounds);
  if (!currentCenter || !targetCenter) {
    applyGoogleMapFit(map, maps, points, padding);
    return;
  }

  const distanceMeters = haversineDistanceMeters(currentCenter, targetCenter);
  if (distanceMeters < 80 || !Number.isFinite(currentZoom)) {
    applyGoogleMapFit(map, maps, points, padding);
    return;
  }

  const endZoom = zoomToFitBounds(targetBounds, readMapSize(map), padding);
  const overviewZoom = resolveFarmMapFlyOverviewZoom({
    distanceMeters,
    currentZoom,
    latitude: targetCenter.lat,
  });
  const start = {
    lat: currentCenter.lat,
    lng: currentCenter.lng,
    zoom: currentZoom,
  };
  const midPoint = midpointLatLng(currentCenter, targetCenter);
  const mid = {
    lat: midPoint.lat,
    lng: midPoint.lng,
    zoom: Math.max(
      7,
      Math.min(overviewZoom, currentZoom - 0.45, endZoom - 0.35)
    ),
  };
  const end = {
    lat: targetCenter.lat,
    lng: targetCenter.lng,
    zoom: endZoom,
  };
  const durationMs = resolveFarmMapFlyDurationMs(distanceMeters);
  const startedAt = performance.now();

  const tick = (now) => {
    if (isCancelled()) return;
    const progress = Math.min(1, (now - startedAt) / durationMs);
    applyMapCamera(map, interpolateFarmMapFlyCamera(progress, start, mid, end));
    if (progress < 1) {
      requestAnimationFrame(tick);
    }
  };

  requestAnimationFrame(tick);
}

export function fitGoogleMapToPoints(map, maps, points, paddingOrOptions = 40) {
  const options =
    typeof paddingOrOptions === "number"
      ? { padding: paddingOrOptions }
      : paddingOrOptions || {};
  const padding = options.padding != null ? options.padding : 40;
  const isCancelled = options.isCancelled || (() => false);

  if (!map || !maps || !points || !points.length) return false;
  const validPoints = points.filter(
    (point) =>
      point &&
      Number.isFinite(Number(point.lat)) &&
      Number.isFinite(Number(point.lng))
  );
  if (!validPoints.length) return false;

  if (options.animate === false || isCancelled()) {
    return applyGoogleMapFit(map, maps, validPoints, padding);
  }

  runFarmMapFly(map, maps, validPoints, padding, isCancelled);
  return true;
}
